/**
 * Header decoration/icon for a preference type category.
 *
 * Belongs here: header SVG or fund list UI.
 * Does not belong here: questionnaire flow.
 */
import type { FundPreferenceType } from '../../setup/types'
import type { Question } from '../../../shared/types/questionnaire.types'
import {
  getPreferenceTypeHeaderStyle,
  getPreferenceTypeDisclaimerColor,
  getFundLabelForPreferenceType,
  formatPortfolioShareAndFundCount,
  getQuestionFundCount,
  getQuestionPortfolioShare
} from '../../setup/config'
import { getPreferenceTypeIcon } from '../getPreferenceTypeIcon'
import { usePreferencesContext } from '../../flow-engine/PreferencesProvider'
import { FundCard } from './FundCard'
import { HeaderProgressBar } from './HeaderProgressBar'
import { FundLabelWithSeparator } from '../header/FundLabelWithSeparator'
import { ImageAvailability } from '../../../shared/types/common.types'
import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import {
  isSectoralThematicLearnMoreQuestion,
  SectoralThematicLearnMoreSheet
} from '../sheets/SectoralThematicLearnMoreSheet'
import { ExpertReviewTopBar } from '../../../shared/components/ExpertReviewTopBar'
import { ExpertReviewScreen } from '../../../analytics/expertReviewEvents'
import { expertReviewSafeAreaTopStyle } from '../../../shared/expertReviewSafeArea'
import { PreferenceTypeHeaderDecoration } from './PreferenceTypeHeaderDecoration'
import { isSegmentIntroQuestion } from '../../../question-utils/preferencesMappers'
import { lightImpactVibrate } from '../../../../../../utils/appBridge'

interface PreferencesPreferenceTypeHeaderProps {
  preferenceType: FundPreferenceType
  question?: Question | null
  progressValue: number
  onBack: () => void
  testLocatorBack?: string
  testLocatorMenu?: string
}

export function PreferencesPreferenceTypeHeader({
  preferenceType,
  question,
  progressValue,
  onBack,
  testLocatorBack,
  testLocatorMenu
}: PreferencesPreferenceTypeHeaderProps) {
  const headerStyle = getPreferenceTypeHeaderStyle(preferenceType.id)
  const isInsight = isSegmentIntroQuestion(question)

  return (
    <header
      className={`shrink-0 px-5 pt-2 relative z-0 overflow-hidden ${isInsight ? 'pb-2' : 'pb-6'}`}
      style={{
        ...headerStyle,
        ...expertReviewSafeAreaTopStyle()
      }}
    >
      <ExpertReviewTopBar
        onBack={onBack}
        menu="preferences"
        analyticsScreen={ExpertReviewScreen.PreferencesQuestionnaire}
        testLocatorBack={testLocatorBack}
        testLocatorMenu={testLocatorMenu}
        className="pb-2"
      />

      <HeaderProgressBar progressValue={progressValue} />
      <PreferencesHeaderContent preferenceType={preferenceType} question={question} />
    </header>
  )
}

export function PreferencesHeaderContent({
  preferenceType,
  question
}: {
  preferenceType: FundPreferenceType
  question?: Question | null
}) {
  const inputType = question?.input?.type
  if (isSegmentIntroQuestion(question)) return null
  if (inputType === 'MULTI_SELECT_GROUPS') {
    return <PreferencesHeaderGrouped preferenceType={preferenceType} question={question} />
  }
  return <PreferencesHeaderDefault preferenceType={preferenceType} />
}

export function PreferencesHeaderDefault({ preferenceType }: { preferenceType: FundPreferenceType }) {
  const fundList = preferenceType.fundList ?? []
  const disclaimerColor = getPreferenceTypeDisclaimerColor(preferenceType.id)
  const { preferenceTypeIconAvailabilityById } = usePreferencesContext()
  const CategoryIcon = getPreferenceTypeIcon(
    preferenceType.id,
    preferenceTypeIconAvailabilityById[preferenceType.id] ?? ImageAvailability.UNKNOWN
  )
  const title = preferenceType.name
  const fundLabel = getFundLabelForPreferenceType(preferenceType)
  const disclaimer = preferenceType.disclaimer?.trim() || undefined
  const totalCount = fundList.length

  return (
    <>
      <PreferenceTypeHeaderDecoration preferenceTypeId={preferenceType.id} />
      <div className="pt-5 text-[#232323]">
        <h1 className="text-[22px] font-[500] pb-1">{title}</h1>
        <p className="text-[12px] font-[400] opacity-60 flex flex-wrap items-center pl-[1px]">
          <FundLabelWithSeparator label={fundLabel} />
        </p>
        {disclaimer ? (
          <p
            className="pt-2 text-[14px] font-[500]"
            style={{ color: disclaimerColor }}
            test-locator="preferences-header-disclaimer"
          >
            {disclaimer}
          </p>
        ) : null}

        {totalCount > 0 ? (
          <div className="flex flex-col items-center w-full pt-5">
            <FundCard
              fund={fundList[0]}
              showCountBadge={totalCount >= 2}
              totalCount={totalCount}
              className="w-full"
              sheetCategoryIcon={<CategoryIcon className="h-10 w-10" />}
              sheetCategoryTitle={title}
              sheetFundLabel={fundLabel}
              sheetFundList={fundList}
            />
            {totalCount > 1 ? (
              <div className="mt-[2px] pt-1 w-[95%] h-2 bg-white rounded-b-lg" />
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  )
}

function getGroupedHeaderDisclaimer(question: Question | null | undefined): string {
  return question?.titleSubText?.trim() ?? ''
}

export function PreferencesHeaderGrouped({
  preferenceType,
  question
}: {
  preferenceType: FundPreferenceType
  question?: Question | null
}) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const title = question?.title?.trim() ?? ''
  const portfolioShare = getQuestionPortfolioShare(question ?? {})
  const fundCount = getQuestionFundCount(question ?? {})
  const fundLabel = formatPortfolioShareAndFundCount(portfolioShare, fundCount)
  const disclaimer = getGroupedHeaderDisclaimer(question)
  const showSectoralLearnMore = isSectoralThematicLearnMoreQuestion(question)

  function handleLearnMoreClick() {
    lightImpactVibrate()
    openBottomSheet(
      <SectoralThematicLearnMoreSheet onDismiss={closeBottomSheet} />,
      closeBottomSheet
    )
  }

  return (
    <>
      <PreferenceTypeHeaderDecoration
        preferenceTypeId={preferenceType.id}
        variant="grouped"
      />
      <div className="pt-5 text-[#232323]">
        {title ? <h1 className="text-[22px] font-[500] pb-1">{title}</h1> : null}
        <p className="text-[14px] font-[400] opacity-60 flex flex-wrap items-center pl-[1px]">
          <FundLabelWithSeparator label={fundLabel} />
        </p>
        {disclaimer ? (
          <p
            className="pt-2 text-[14px] font-[500] opacity-60"
            test-locator="preferences-header-disclaimer"
          >
            {disclaimer}
            {showSectoralLearnMore ? (
              <>
                {' '}
                <span
                  onClick={handleLearnMoreClick}
                  className="underline cursor-pointer"
                  test-locator="preferences-header-learn-more"
                >
                  Learn More
                </span>
              </>
            ) : null}
          </p>
        ) : null}
      </div>
    </>
  )
}
